import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiStar } from "react-icons/fi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Testimonials.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import { CardSkeleton } from "../common/Skeleton";

function Stars({ value }) {
  return (
    <div className="tsStars" aria-label={`${value} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`tsStar ${i < value ? "isOn" : ""}`}>
          <FiStar />
        </span>
      ))}
    </div>
  );
}

function Avatar({ name, image }) {
  if (image) {
    return <img className="tsAvatar" src={`${apis().localUrl}${image}`} alt={name} />;
  }
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
  return <div className="tsAvatar">{initials}</div>;
}

function TestimonialCard({ item }) {
  return (
    <div className="tsCard">
      <div className="tsCard__top">
        <div className="tsCard__who">
          <Avatar name={item.name} image={item.image} />
          <div>
            <div className="tsCard__name">{item.name}</div>
            <div className="tsCard__meta">
              <span className="tsCard__role">{item.role}</span>
              {item.area ? <><span className="tsDot" /><span className="tsCard__area">{item.area}</span></> : null}
            </div>
          </div>
        </div>
        <Stars value={item.rating || 5} />
      </div>
      <p className="tsCard__text">“{item.text}”</p>
    </div>
  );
}

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadReviews = async () => {
      setLoading(true);
      const result = await httpAction({ url: apis().reviewList, silent: true });
      setLoading(false);
      if (result?.status) setReviews(result.list || []);
    };
    loadReviews();
  }, []);

  return (
    <section className="ts">
      <div className="container">
        <motion.div className="ts__head" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.35 }} transition={{ duration: 0.5, ease: "easeOut" }}>
          <div>
            <span className="ts__kicker">Testimonials</span>
            <h2 className="ts__title">What Our Clients Say</h2>
            <p className="ts__sub">Real reviews from buyers, investors, and clients who worked with our team.</p>
          </div>
          <Link to="/contact" className="ts__cta">Speak to an Agent <FiArrowUpRight /></Link>
        </motion.div>
        {loading ? (
          <CardSkeleton count={3} height={250} />
        ) : (
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.55, ease: "easeOut" }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]} // Ensure Autoplay module is added
              autoplay={{
                delay: 1000, // Change to the desired delay (4000ms = 4 seconds)
                disableOnInteraction: false, // Autoplay continues even after user interaction
              }}
              pagination={{ clickable: true }}
              spaceBetween={16}
              slidesPerView={1}
              breakpoints={{
                576: { slidesPerView: 1.1 },
                768: { slidesPerView: 2 },
                992: { slidesPerView: 2.4 },
                1200: { slidesPerView: 3 },
              }}
              className="ts__swiper"
            >
              {reviews.map((t) => (
                <SwiperSlide key={t._id}><TestimonialCard item={t} /></SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        )}
      </div>
    </section>
  );
}